import { SelectChangeEvent } from '@mui/material';
import { ChangeEvent, useMemo, useState } from 'react';

import {
  CreateOrganizationData,
  OrganizationsDetails,
  UpdateOrganizationData,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
} from '@verantos-dev/data-access-organizations';

import { CreateEditOrganizationType } from '../components/OrganizationsListCreateEditDialog';
import {
  DialogActionType,
  useOrganizationsListDialogContext,
} from '../context/OrganizationsListDialogContext';

export type editFields = {
  name: string;
  type: string;
  subType: string;
};

export enum OrganizationType {
  CUSTOMER = 'Customer',
  RESEARCH_PARTNER = 'Research partner',
  VERANTOS = 'Verantos',
}

export enum OrganizationCustomerSubType {
  MEDICAL_DEVICE_MANUFACTURER = 'Medical device manufacturer',
  PHARMACEUTICAL_COMPANY = 'Pharmaceutical company',
}

export enum OrganizationResearchPartnerSubType {
  CLAIMS_VENDOR = 'Claims vendor',
  HEALTH_SYSTEM = 'Health system',
  REGISTRY = 'Registry',
}

export const getSubTypeLabel = (subType: string) => {
  if (subType in OrganizationCustomerSubType) {
    return OrganizationCustomerSubType[subType as keyof typeof OrganizationCustomerSubType];
  } else if (subType in OrganizationResearchPartnerSubType) {
    return OrganizationResearchPartnerSubType[
      subType as keyof typeof OrganizationResearchPartnerSubType
    ];
  }
  return subType;
};

export const useOrganizationsListCreateEditDialog = (
  dialogType: CreateEditOrganizationType = 'createOrganization',
  organizationData?: OrganizationsDetails
) => {
  const { dispatch } = useOrganizationsListDialogContext();

  const [name, setName] = useState(
    dialogType === 'editOrganization' ? organizationData?.displayName : ''
  );
  const [type, setType] = useState(dialogType === 'editOrganization' ? organizationData?.type : '');
  const [subType, setSubType] = useState(
    dialogType === 'editOrganization' ? organizationData?.subType : ''
  );
  const [organizationKey, setOrganizationKey] = useState(
    dialogType === 'editOrganization' ? organizationData?.organizationKey : ''
  );

  // custom app error state
  const [appError, setAppError] = useState<string | undefined>(undefined);

  const typeOptions = Object.entries(OrganizationType)
    .filter(([, value]) => value !== OrganizationType.VERANTOS)
    .map(([key, value]) => {
      return {
        value: key,
        displayValue: value,
      };
    });

  const subTypeOptions = useMemo(() => {
    if (type === 'CUSTOMER') {
      return Object.entries(OrganizationCustomerSubType).map(([key, value]) => ({
        value: key,
        displayValue: value.toString(),
      }));
    } else if (type === 'RESEARCH_PARTNER') {
      return Object.entries(OrganizationResearchPartnerSubType).map(([key, value]) => ({
        value: key,
        displayValue: value.toString(),
      }));
    }
    return [];
  }, [type]);

  // input handlers
  const onChangeName = (_: string, value: string) => {
    setName(value);
  };

  const onChangeType = (event: SelectChangeEvent<string>) => {
    setSubType('');
    setType(event.target.value);
  };

  const onChangeSubType = (event: SelectChangeEvent<string>) => {
    setSubType(event.target.value);
  };

  const onChangeOrganizationKey = (_: string, updatedOrganizationKey: string) => {
    setOrganizationKey(updatedOrganizationKey);
  };

  // button handlers
  const onCancel = () => {
    dispatch({ type: DialogActionType.CLOSE_DIALOG });
  };

  // User creation mutation hook
  const [
    createOrganization,
    { isLoading: isCreateOrganizationLoading, error: createOrganizationError },
  ] = useCreateOrganizationMutation();

  // User update user mutation hook
  const [
    updateOrganization,
    { isLoading: isUpdateOrganizationLoading, error: updateOrganizationError },
  ] = useUpdateOrganizationMutation();

  const isDataEdited = useMemo(() => {
    if (dialogType === 'editOrganization' && organizationData) {
      return (
        organizationData.displayName !== name ||
        organizationData.type !== type ||
        organizationData.subType !== subType ||
        organizationData.organizationKey !== organizationKey
      );
    }
    return true;
  }, [dialogType, organizationData, name, type, subType, organizationKey]);

  const nameErrorCode = useMemo(() => {
    const nameTrimmed = name?.trim();
    if (name && name.toLowerCase() === OrganizationType.VERANTOS.toLowerCase()) {
      return 'organizationNameVerantosError';
    }
    if (nameTrimmed && !/^[a-zA-Z0-9_\- ]*$/.test(nameTrimmed)) {
      return 'organizationNameInvalidCharactersError';
    }
    if (
      nameTrimmed &&
      (nameTrimmed.startsWith('_') ||
        nameTrimmed.startsWith('-') ||
        nameTrimmed.endsWith('_') ||
        nameTrimmed.endsWith('-'))
    ) {
      return 'organizationNameStartEndCharactersError';
    }
    return '';
  }, [name]);

  const validToCreateEdit = () => {
    return name?.trim() !== '' && type !== '' && !nameErrorCode && isDataEdited;
  };

  // handle form submission
  const onSubmit = async () => {
    // make sure the data is valid
    if (!validToCreateEdit()) {
      return;
    }

    if (dialogType === 'editOrganization' && organizationData) {
      const data: UpdateOrganizationData['data'] = {
        name: name?.trim() || '',
        type: type || '',
        subType: subType || '',
        organizationKey: organizationKey?.trim() || '',
        attributes: [],
      };

      try {
        const result = await updateOrganization({
          organizationId: organizationData.organizationId,
          data: data,
        });
        const updateError = result?.error as { data?: { message?: string } } | undefined;
        if (typeof updateError?.data?.message === 'string') {
          setAppError(updateError.data?.message);
          return;
        }
      } catch (error) {
        console.error('Error updating user', error);
      }
    } else if (dialogType === 'createOrganization') {
      // bundle up the data
      const data: CreateOrganizationData = {
        name: name?.trim() || '',
        type: type || '',
        subType: subType || '',
        organizationKey: organizationKey?.trim() || '',
        attributes: [],
      };

      try {
        const result = await createOrganization(data);
        const createError = result?.error as { data?: { message?: string } } | undefined;
        if (typeof createError?.data?.message === 'string') {
          setAppError(createError.data?.message);
          return;
        }
      } catch (error) {
        console.error('Error creating user', error);
      }
    }
  };

  return {
    name,
    type,
    subType,

    onChangeName,
    onChangeType,
    onChangeSubType,

    nameErrorCode,

    isApiLoading: isCreateOrganizationLoading || isUpdateOrganizationLoading,
    apiError: createOrganizationError || updateOrganizationError,
    appErrorMessage: appError ? appError : undefined,

    validToCreateEdit: validToCreateEdit(),

    typeOptions,
    subTypeOptions,
    getSubTypeLabel,

    organizationKey,
    onChangeOrganizationKey,

    onCancel,
    onSubmit,
  };
};
